import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { applyUiLanguage, readInitialLanguage } from "../i18n/i18n";
import { activeSessionAdapter } from "../session/dealSessionAdapter";
import { DEFAULT_THEME_INDEX, randomThemeIndex } from "../session/themes";
import type {
  DiscardHistoryEntry,
  PlayerCount,
  SessionBootstrap,
  SessionCard,
  UiLanguage
} from "../session/types";

export type AppScreen =
  | "home"
  | "create"
  | "join"
  | "room"
  | "seat"
  | "theme"
  | "hand"
  | "reveal"
  | "history";

export type RevealMode = "discard" | "history";

interface CreateDraft {
  playerCount: PlayerCount;
  themeIndex: number;
}

interface AppState {
  screen: AppScreen;
  uiLanguage: UiLanguage;
  createDraft: CreateDraft;
  session: SessionBootstrap | null;
  selectedSeat: number | null;
  seatLocked: boolean;
  hand: SessionCard[];
  penaltyQueue: SessionCard[];
  history: DiscardHistoryEntry[];
  nextHistorySequence: number;
  handCovered: boolean;
  handIntroPending: boolean;
  reviewingTheme: boolean;
  selectedCardId: string | null;
  lastDrawnCardId: string | null;
  revealCardId: string | null;
  revealMode: RevealMode | null;
  isCreating: boolean;
  sessionError: boolean;
}

interface AppActions {
  setUiLanguage(language: UiLanguage): void;
  openCreate(): void;
  openJoin(): void;
  goHome(): void;
  resumeSession(): void;
  setPlayerCount(playerCount: PlayerCount): void;
  setThemeIndex(themeIndex: number): void;
  randomizeTheme(): void;
  startSession(): Promise<void>;
  proceedToSeat(): void;
  joinSession(sessionCode: string): Promise<void>;
  selectSeat(seat: number): void;
  confirmSeat(): void;
  showHand(): void;
  toggleCover(): void;
  revealHandIntro(): void;
  openThemeReview(): void;
  toggleCard(cardId: string): void;
  openReveal(cardId: string, mode: RevealMode): void;
  cancelReveal(): void;
  confirmReveal(): void;
  discardCard(cardId: string): void;
  drawPenalty(): boolean;
  openHistory(): void;
  closeHistory(): void;
  undoLastDiscard(): void;
  resetSession(): void;
}

export type AppStore = AppState & AppActions;

interface PersistedAppState {
  readonly screen: AppScreen;
  readonly uiLanguage: UiLanguage;
  readonly createDraft: CreateDraft;
  readonly session: SessionBootstrap | null;
  readonly selectedSeat: number | null;
  readonly seatLocked: boolean;
  readonly hand: readonly SessionCard[];
  readonly penaltyQueue: readonly SessionCard[];
  readonly history: readonly DiscardHistoryEntry[];
  readonly nextHistorySequence: number;
}

// On reload, any in-game screen returns to a covered hand so nothing is exposed.
const PRIVATE_SCREENS = new Set<AppScreen>(["hand", "reveal", "history"]);

const initialData: AppState = {
  screen: "home",
  uiLanguage: readInitialLanguage(),
  createDraft: { playerCount: 4, themeIndex: DEFAULT_THEME_INDEX },
  session: null,
  selectedSeat: null,
  seatLocked: false,
  hand: [],
  penaltyQueue: [],
  history: [],
  nextHistorySequence: 1,
  handCovered: true,
  handIntroPending: false,
  reviewingTheme: false,
  selectedCardId: null,
  lastDrawnCardId: null,
  revealCardId: null,
  revealMode: null,
  isCreating: false,
  sessionError: false
};

const freshSessionState: Omit<
  AppState,
  "screen" | "uiLanguage" | "createDraft" | "session"
> = {
  selectedSeat: null,
  seatLocked: false,
  hand: [],
  penaltyQueue: [],
  history: [],
  nextHistorySequence: 1,
  handCovered: true,
  handIntroPending: false,
  reviewingTheme: false,
  selectedCardId: null,
  lastDrawnCardId: null,
  revealCardId: null,
  revealMode: null,
  isCreating: false,
  sessionError: false
};

export const useAppStore = create<AppStore>()(
  persist<AppStore, [], [], PersistedAppState>(
    (set, get) => ({
      ...initialData,
      setUiLanguage: (language) => {
        applyUiLanguage(language);
        set({ uiLanguage: language });
      },
      openCreate: () => set({ screen: "create", sessionError: false }),
      openJoin: () => set({ screen: "join", sessionError: false }),
      goHome: () =>
        set({
          screen: "home",
          selectedCardId: null,
          revealCardId: null,
          revealMode: null
        }),
      resumeSession: () => {
        const state = get();
        if (state.session === null) {
          set({ screen: "home" });
          return;
        }
        if (state.seatLocked) {
          set({ screen: "hand", handCovered: true });
          return;
        }
        set({ screen: "seat" });
      },
      setPlayerCount: (playerCount) =>
        set((state) => ({
          createDraft: { ...state.createDraft, playerCount }
        })),
      setThemeIndex: (themeIndex) =>
        set((state) => ({ createDraft: { ...state.createDraft, themeIndex } })),
      randomizeTheme: () =>
        set((state) => ({
          createDraft: { ...state.createDraft, themeIndex: randomThemeIndex() }
        })),
      startSession: async () => {
        set({ isCreating: true, sessionError: false });
        try {
          const session = await activeSessionAdapter.createSession({
            playerCount: get().createDraft.playerCount,
            themeIndex: get().createDraft.themeIndex
          });
          set({ session, screen: "room", ...freshSessionState });
        } catch {
          set({ screen: "create", isCreating: false, sessionError: true });
        }
      },
      proceedToSeat: () => {
        if (get().session !== null) {
          set({ screen: "seat" });
        }
      },
      joinSession: async (sessionCode) => {
        set({ isCreating: true, sessionError: false });
        try {
          const session = await activeSessionAdapter.joinSession(sessionCode);
          set({
            session,
            createDraft: {
              playerCount: session.playerCount,
              themeIndex: get().createDraft.themeIndex
            },
            screen: "seat",
            ...freshSessionState
          });
        } catch {
          set({ isCreating: false, sessionError: true });
        }
      },
      selectSeat: (seat) => {
        const session = get().session;
        if (
          session === null ||
          get().seatLocked ||
          seat < 1 ||
          seat > session.playerCount
        ) {
          return;
        }
        set({ selectedSeat: seat });
      },
      confirmSeat: () => {
        const { session, selectedSeat } = get();
        const seatDeal = session?.seatDeals.find(
          (deal) => deal.seat === selectedSeat
        );
        if (session === null || selectedSeat === null || seatDeal === undefined) {
          return;
        }
        set({
          seatLocked: true,
          hand: [...seatDeal.initial],
          penaltyQueue: [...seatDeal.penalty],
          history: [],
          nextHistorySequence: 1,
          handCovered: true,
          handIntroPending: true,
          reviewingTheme: false,
          screen: "theme"
        });
      },
      // The hand is always the real screen; covering flips the cards face-down
      // (identical backs) rather than opening a separate cover screen.
      showHand: () => set({ screen: "hand" }),
      toggleCover: () =>
        set((state) => ({
          handCovered: !state.handCovered,
          handIntroPending: false,
          selectedCardId: null,
          lastDrawnCardId: null
        })),
      // First-deal flourish: the hand opens covered, then flips itself face-up
      // so players see it was just dealt instead of stalling on a hidden hand.
      revealHandIntro: () =>
        set({ handCovered: false, handIntroPending: false }),
      openThemeReview: () => set({ screen: "theme", reviewingTheme: true }),
      toggleCard: (cardId) => {
        const state = get();
        if (state.handCovered || !state.hand.some((c) => c.cardId === cardId)) {
          return;
        }
        set({
          selectedCardId: state.selectedCardId === cardId ? null : cardId,
          lastDrawnCardId: null
        });
      },
      openReveal: (cardId, mode) => {
        const state = get();
        if (mode === "discard" && state.handCovered) {
          return;
        }
        const isAvailable =
          mode === "discard"
            ? state.hand.some((card) => card.cardId === cardId)
            : state.history.some((entry) => entry.card.cardId === cardId);
        if (!isAvailable) {
          return;
        }
        set({ screen: "reveal", revealCardId: cardId, revealMode: mode });
      },
      cancelReveal: () =>
        set((state) => ({
          screen: state.revealMode === "history" ? "history" : "hand",
          revealCardId: null,
          revealMode: null,
          selectedCardId: null
        })),
      confirmReveal: () => {
        const state = get();
        if (state.revealMode === "history") {
          set({ screen: "history", revealCardId: null, revealMode: null });
          return;
        }
        const card = state.hand.find(
          (candidate) => candidate.cardId === state.revealCardId
        );
        if (card === undefined) {
          return;
        }
        const entry: DiscardHistoryEntry = {
          historyId: `local-history-${state.nextHistorySequence}`,
          card,
          reason: "LOCAL_DISCARD"
        };
        set({
          hand: state.hand.filter((candidate) => candidate.cardId !== card.cardId),
          history: [...state.history, entry],
          nextHistorySequence: state.nextHistorySequence + 1,
          selectedCardId: null,
          lastDrawnCardId: null,
          revealCardId: null,
          revealMode: null,
          screen: "hand"
        });
      },
      discardCard: (cardId) => {
        const state = get();
        const card = state.hand.find((candidate) => candidate.cardId === cardId);
        if (card === undefined) return;
        set({
          hand: state.hand.filter((candidate) => candidate.cardId !== cardId),
          history: [
            ...state.history,
            {
              historyId: `local-history-${state.nextHistorySequence}`,
              card,
              reason: "LOCAL_DISCARD"
            }
          ],
          nextHistorySequence: state.nextHistorySequence + 1,
          selectedCardId: null,
          lastDrawnCardId: null
        });
      },
      drawPenalty: () => {
        const state = get();
        const card = state.penaltyQueue[0];
        if (card === undefined) {
          return false;
        }
        set({
          hand: [...state.hand, card],
          penaltyQueue: state.penaltyQueue.slice(1),
          selectedCardId: null,
          lastDrawnCardId: card.cardId
        });
        return true;
      },
      openHistory: () => set({ screen: "history", selectedCardId: null }),
      closeHistory: () => set({ screen: "hand", lastDrawnCardId: null }),
      undoLastDiscard: () => {
        const state = get();
        const entry = state.history.at(-1);
        if (entry === undefined) {
          return;
        }
        set({
          hand: [...state.hand, entry.card],
          history: state.history.slice(0, -1),
          selectedCardId: null,
          lastDrawnCardId: entry.card.cardId
        });
      },
      resetSession: () =>
        set({
          ...initialData,
          uiLanguage: get().uiLanguage,
          createDraft: get().createDraft
        })
    }),
    {
      name: "ysi.web.local-session.v2",
      version: 3,
      storage: createJSONStorage<PersistedAppState>(() => localStorage),
      partialize: (state) => ({
        screen: state.screen,
        uiLanguage: state.uiLanguage,
        createDraft: state.createDraft,
        session: state.session,
        selectedSeat: state.selectedSeat,
        seatLocked: state.seatLocked,
        hand: state.hand,
        penaltyQueue: state.penaltyQueue,
        history: state.history,
        nextHistorySequence: state.nextHistorySequence
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PersistedAppState>;
        const draft =
          persisted.createDraft !== undefined &&
          typeof persisted.createDraft.playerCount === "number"
            ? {
                playerCount: persisted.createDraft.playerCount,
                themeIndex:
                  typeof persisted.createDraft.themeIndex === "number"
                    ? persisted.createDraft.themeIndex
                    : DEFAULT_THEME_INDEX
              }
            : currentState.createDraft;
        const screen = persisted.screen ?? currentState.screen;
        const protectedScreen =
          persisted.session != null && PRIVATE_SCREENS.has(screen)
            ? "hand"
            : screen;

        applyUiLanguage(persisted.uiLanguage ?? currentState.uiLanguage);
        return {
          ...currentState,
          ...persisted,
          createDraft: draft,
          hand: [...(persisted.hand ?? [])],
          penaltyQueue: [...(persisted.penaltyQueue ?? [])],
          history: [...(persisted.history ?? [])],
          screen: protectedScreen,
          handCovered: true,
          handIntroPending: false,
          reviewingTheme: false,
          selectedCardId: null,
          lastDrawnCardId: null,
          revealCardId: null,
          revealMode: null,
          isCreating: false,
          sessionError: false
        };
      }
    }
  )
);
